import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ModalContainer, ModalClose } from 'shared/modals';
import { useDispensary } from 'src/hooks/use-dispensary';
import { AssetLibraryModal } from 'src/components/asset-library-modal';
import type { SelectedAsset } from 'src/components/asset-library-modal';
import { Categories, Category } from '../categories.types';
import { UploadImage } from './upload-image';
import { ImageLibrary } from './image-library';

type SelectImageModalProps = {
  onClose: () => void;
  category: Category;
  categories: Categories;
  onSave: (updatedCategories: Categories) => void;
};

export const SelectImageModal = ({
  onClose,
  category,
  categories,
  onSave,
}: SelectImageModalProps): JSX.Element | null => {
  const { category: value, image, cardImage, isOutlet } = category;
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState(false);
  const closeModal = isUploadImageModalOpen ? () => setIsUploadImageModalOpen(false) : onClose;
  // For outlet collections, default to cardImage, otherwise use regular image
  const [selectedImage, setSelectedImage] = useState(isOutlet && cardImage ? cardImage : image);

  const flags = useFlags();
  const showAssetLibrary = flags['growth.ecomm.asset-library.rollout'] ?? false;
  const dispensary = useDispensary();
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? '';
  const useLibraryFlow = showAssetLibrary && Boolean(enterpriseId);

  const saveImage = (imageToSave: string = selectedImage): void => {
    const updatedCategories = categories.map((obj) => {
      if (obj.category === value) {
        if (obj.isOutlet) {
          const updated = {
            category: obj.category,
            image: obj.image, // Keep original image field
            collectionId: obj.collectionId,
            isOutlet: obj.isOutlet,
            cardImage: imageToSave,
            cardDisplayName: obj.cardDisplayName,
          };
          return updated;
        }

        // For regular categories, update image field
        const updated = {
          category: obj.category,
          image: imageToSave,
          collectionId: obj.collectionId,
          // Preserve outlet fields if they exist (they shouldn't, but just in case)
          ...(obj.isOutlet !== undefined && { isOutlet: obj.isOutlet }),
          ...(obj.cardImage && { cardImage: obj.cardImage }),
          ...(obj.cardDisplayName && { cardDisplayName: obj.cardDisplayName }),
        };
        return updated;
      }
      return obj;
    });

    onSave(updatedCategories);
    onClose();
  };

  const openUploadImageModal = (): void => {
    setIsUploadImageModalOpen(true);
  };

  const handleLibrarySelect = (asset: SelectedAsset): void => {
    setSelectedImage(asset.url);
    saveImage(asset.url);
  };

  if (useLibraryFlow) {
    return (
      <AssetLibraryModal
        enterpriseId={enterpriseId}
        isOpen
        onClose={onClose}
        onSelect={handleLibrarySelect}
        title='Select Category Image'
        description='Choose an asset from your enterprise library for this category'
      />
    );
  }

  return (
    <ModalContainer
      isOpen
      onRequestClose={closeModal}
      width='min(880px, calc(100vw - 32px))'
      alignItems='flex-start'
      padding='40px'
      ariaHideApp={false}
      textAlign='left'
      scrollable
    >
      <ModalClose onClick={closeModal} />

      {isUploadImageModalOpen ? (
        <UploadImage
          category={category}
          setSelectedImage={setSelectedImage}
          setIsUploadImageModalOpen={setIsUploadImageModalOpen}
        />
      ) : (
        <ImageLibrary
          category={category}
          saveImage={saveImage}
          openUploadImageModal={openUploadImageModal}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />
      )}
    </ModalContainer>
  );
};
